import React, { useEffect, useRef, useState, } from 'react'
import ReCAPTCHA from 'react-google-recaptcha';
import HtmlParser from 'react-html-parser';
import {
    awaitDelay,
    getError, getLanguageObj, getResponse, isUndNullEmpNew, LoadingSection, sendMessageCloseCurrentWindow, settings, settingsPassword,
    validateEmail, validatePhoneNumber
} from '../../helpers/UtilsHelper';
import useAuth from '../../auth/useAuth';
import RegisterNewUserMessage from './RegisterNewUserMessage';
import Menu from '../main/Menu';
import { AccountClass } from '../../classes/account/accountClass';
import { GlobalFormats } from '../../classes/main/globalFormatClass';
import { LoginService } from '../../services/login/LoginService';

const { app, firebase, icons, messages, labels, } = settings;
const { urls, methods } = app?.backend;
const { fields: dbFields, } = firebase?.firestore;

const currentMethod = 'RegisterNewUser - function';

export default function RegisterNewUser({
    languageObj = GlobalFormats?.getClass(getLanguageObj({ subMethod: currentMethod })?.result),
} = {}) {
    //#region variables
    const auth = useAuth()
    const [account, setAccount] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        password: '',
        passwordConfirm: '',
    });
    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        password: '',
        passwordConfirm: '',
    });
    const [process, setProcess] = useState({
        showLoading: false,
        save: false,
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showRegisterMessage, setShowRegisterMessage] = useState(false);

    const isMount = useRef(true);
    const reCaptchaRef = useRef(ReCAPTCHA);
    //#endregion variables

    //#region methods
    const registerAccount = async ({
        e = {},
        subMethod = '',
    }) => {
        const currentMethod = 'registerAccount';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault();

            //#region form validate
            response = formDataValidate({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;
            //#endregion form validate

            setShowPassword(false);
            setProcess(prev => ({ ...prev, showLoading: true, save: true }));

            //#region reCAPTCHA
            const reCaptchaToken = await reCaptchaRef.current.executeAsync();
            reCaptchaRef.current.reset();
            //#endregion reCAPTCHA

            //#region request
            response = await LoginService?.registerAccount({
                firstName: account?.firstName,
                lastName: account?.lastName,
                phoneNumber: account?.phoneNumber,
                email: account?.email,
                password: account?.password,
                passwordConfirm: account?.passwordConfirm,
                reCaptchaToken: reCaptchaToken,
                languageId: languageObj?.id,
                subMethod: currentMethod
            })
            if (!response?.isSuccess || response?.isWarning) return;
            //#endregion request

            setAccount(response?.result?.accountMl || {});
            setShowRegisterMessage(true);
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false, save: false }));

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    }

    const formDataValidate = ({
        subMethod = ''
    } = {}) => {
        const currentMethod = 'formDataValidate';
        let response = getResponse({ type: 0 });
        try {
            const requiredMsg = messages?.thisFieldRequired?.[languageObj?.id];
            const errors_ = {
                firstName: '',
                lastName: '',
                phoneNumber: '',
                email: '',
                password: '',
                passwordConfirm: '',
            };

            account.phoneNumber = account?.phoneNumber?.replaceAll(' ', '');

            if (isUndNullEmpNew({ data: account?.firstName, subMethod: currentMethod })?.result) {
                errors_.firstName = requiredMsg;
                response = getResponse({ type: 1 });
            }

            if (isUndNullEmpNew({ data: account?.lastName, subMethod: currentMethod })?.result) {
                errors_.lastName = requiredMsg;
                response = getResponse({ type: 1 });
            }

            if (isUndNullEmpNew({ data: account?.phoneNumber, subMethod: currentMethod })?.result) {
                errors_.phoneNumber = requiredMsg;
                response = getResponse({ type: 1 });
            } else if (!validatePhoneNumber(account?.phoneNumber)) {
                errors_.phoneNumber = `${messages?.youMustEnterValidPhoneNumber?.[languageObj?.id]}<br/>`;
                errors_.phoneNumber += `${labels?.example?.[languageObj?.id]}: <b>+1 123 1234567</b><br/>`;
                errors_.phoneNumber += `${labels?.code?.[languageObj?.id]}: <b>+1</b><br/>`;
                errors_.phoneNumber += `${labels?.code?.[languageObj?.id]}: <b>123</b><br/>`;
                errors_.phoneNumber += `${labels?.nickName?.[languageObj?.id]}: <b>1234567</b><br/>`;
                response = getResponse({ type: 1 });
            }

            if (isUndNullEmpNew({ data: account?.email, subMethod: currentMethod })?.result) {
                errors_.email = requiredMsg;
                response = getResponse({ type: 1 });
            } else if (!validateEmail(account?.email)) {
                errors_.email = messages?.youMustEnterValidEmail?.[languageObj?.id];
                response = getResponse({ type: 1 });
            }

            if (isUndNullEmpNew({ data: account?.password, subMethod: currentMethod })?.result) {
                errors_.password = requiredMsg;
                response = getResponse({ type: 1 });
            } else if (account?.password?.length < settingsPassword.maxLength) {
                errors_.password = `${messages?.youMustEnterValidPassword?.[languageObj?.id]} ${messages?.passwordMinimunCharacters?.[languageObj?.id]}`;
                response = getResponse({ type: 1 });
            }

            if (isUndNullEmpNew({ data: account?.passwordConfirm, subMethod: currentMethod })?.result) {
                errors_.passwordConfirm = requiredMsg;
                response = getResponse({ type: 1 });
            } else if (account?.passwordConfirm?.length < settingsPassword.maxLength) {
                errors_.passwordConfirm = `${messages?.youMustEnterValidPasswordConfirm?.[languageObj?.id]} ${messages?.passwordMinimunCharacters?.[languageObj?.id]}`;
                response = getResponse({ type: 1 });
            }

            if ((errors_?.password === '' && errors_?.passwordConfirm === '') && (account?.password !== account?.passwordConfirm)) {
                errors_.passwordConfirm = messages?.passwordAndPasswordConfirmNotMatch?.[languageObj?.id];
                response = getResponse({ type: 1 });
            }

            setErrors(errors_);
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
        return response;
    }

    const goTo = async ({
        e = {},
        subMethod = '',
    } = {}) => {
        const currentMethod = 'goTo';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault();
            setProcess(prev => ({ ...prev, showLoading: true, home: subMethod === 'btnHome', login: subMethod === 'btnLogin' }));
            await awaitDelay({ delayTime: settings?.timers?.oneSeconds });
            window.location.href = subMethod === 'btnHome' ? urls?.home : settings?.app?.backend?.urls?.login;
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false, home: false, login: false }));

            await sendMessageCloseCurrentWindow({
                response: response,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    }

    const getData = async ({
        subMethod = ''
    } = {}) => {
        const currentMethod = 'getData';
        let response = getResponse({ type: 0 });
        try {
            setAccount({
                ...AccountClass?.getClass(),
                passwordConfirm: '',
                method: methods?.accounts?.register,
            });
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    }

    useEffect(() => {
        isMount.current = true;
        getData({ subMethod: 'form - onLoad' });
        return () => {
            isMount.current = false;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    //#endregion methods

    return (
        <div>
            <Menu viewName={'home'} process={''} />

            <div className='content-wrapper bg-transparent' style={{ minHeight: '85vh' }}>

                {/* Content Header (Page header) */}
                <div className='content-header'>
                    <div className='container-fluid'>
                        <div className='row mb-2 align-items-center bg-white p-3 rounded shadow-lg mx-0'>
                            <div className='col-sm-6'>
                                <h4 className='m-0 font-weight-bold text-dark'>{labels?.registerNewMembership?.[languageObj?.id]}</h4>
                            </div>
                            <div className='col-sm-6'>
                                <ol className='breadcrumb float-sm-right bg-transparent mb-0'>
                                    <li className='breadcrumb-item'><a href={urls?.home}>{labels?.home?.[languageObj?.id]}</a></li>
                                    <li className='breadcrumb-item active text-primary'>{labels?.registerNewMembership?.[languageObj?.id]}</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
                {/* /.content-header */}

                {/* Main content */}
                <section className='content'>
                    <div className='container-fluid'>
                        <div className='row'>
                            <div className='col-12'>
                                {!showRegisterMessage ? (
                                    <div className='login-box login-box-container'>

                                        <div className={settings?.html?.cards?.default} >
                                            <LoadingSection alt='loadingSection'
                                                className='overlay dark text-white'
                                                height={20}
                                                hidden={!process?.showLoading}
                                                width={20}
                                                languageId={languageObj?.id}
                                                subMethod={currentMethod} />

                                            {/* ✅ Premium Card Header (Rule 12) */}
                                            <div className='card-header bg-white border-bottom-0 pt-4 px-4'>
                                                <div className='d-flex align-items-center'>
                                                    <div className='bg-light p-2 rounded-circle mr-3 shadow-sm'
                                                        style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <img alt='UserLogin'
                                                            style={{ width: '25px', height: '25px', objectFit: 'contain' }}
                                                            src={settings?.images?.userLogin} />
                                                    </div>
                                                    <div className='d-flex flex-column'>
                                                        <h5 className='mb-0 font-weight-bold text-dark'>{labels?.registry?.[languageObj?.id]}</h5>
                                                        <div className='mt-1'>
                                                            <span className='badge badge-pill badge-primary px-2 py-1 text-uppercase' style={settings?.html?.badges?.micro}>
                                                                <i className={`${icons?.fasFa?.userCheck} mr-1`}></i> {labels?.createAccount?.[languageObj?.id]}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <form onSubmit={(e) => registerAccount({ e: e, subMethod: 'form - onSubmit' })} method='post'>

                                                {/*Body*/}
                                                <div className='card-body'>

                                                    <h6>
                                                        <b>{labels?.personalInformation?.[languageObj?.id]}:</b>
                                                    </h6>
                                                    <hr />

                                                    <div className='form-group mb-4'>
                                                        <label className='small font-weight-bold text-uppercase form-label-uppercase'>{labels?.firstName?.[languageObj?.id]}</label>
                                                        <div className='position-relative'>
                                                            <i className={`${icons?.fasFa?.user} position-absolute input-icon-left`} />
                                                            <input autoFocus
                                                                className='form-control border-0 shadow-sm form-input-icon'
                                                                onChange={(e) => setAccount({ ...account, [dbFields?.firstName]: e.target.value, })}
                                                                onFocus={() => setErrors(prev => ({ ...prev, firstName: '' }))}
                                                                placeholder={labels?.firstName?.[languageObj?.id]}
                                                                title={labels?.firstName?.[languageObj?.id]}
                                                                value={account?.firstName || ''} />
                                                        </div>
                                                        <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.firstName)}</div>
                                                    </div>

                                                    <div className='form-group mb-4'>
                                                        <label className='small font-weight-bold text-uppercase form-label-uppercase'>{labels?.lastName?.[languageObj?.id]}</label>
                                                        <div className='position-relative'>
                                                            <i className={`${icons?.fasFa?.user} position-absolute input-icon-left`} />
                                                            <input className='form-control border-0 shadow-sm form-input-icon'
                                                                onChange={(e) => setAccount({ ...account, [dbFields?.lastName]: e.target.value })}
                                                                onFocus={() => setErrors(prev => ({ ...prev, lastName: '' }))}
                                                                placeholder={labels?.lastName?.[languageObj?.id]}
                                                                title={labels?.lastName?.[languageObj?.id]}
                                                                value={account?.lastName || ''} />
                                                        </div>
                                                        <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.lastName)}</div>
                                                    </div>

                                                    <div className='form-group mb-4'>
                                                        <label className='small font-weight-bold text-uppercase form-label-uppercase'>{labels?.phoneNumber?.[languageObj?.id]}</label>
                                                        <div className='position-relative'>
                                                            <i className={`${icons?.fasFa?.mobileAlt} position-absolute input-icon-left`} />
                                                            <input className='form-control border-0 shadow-sm form-input-icon'
                                                                onChange={(e) => setAccount({ ...account, [dbFields?.phoneNumber]: e.target.value, })}
                                                                onFocus={() => setErrors(prev => ({ ...prev, phoneNumber: '' }))}
                                                                placeholder={labels?.phoneNumberPlaceholder?.[languageObj?.id]}
                                                                title={labels?.phoneNumber?.[languageObj?.id]}
                                                                value={account?.phoneNumber || ''} />
                                                        </div>
                                                        <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.phoneNumber)}</div>
                                                    </div>

                                                    <h6><b>{labels?.accountInformation?.[languageObj?.id]}:</b></h6>
                                                    <hr />

                                                    <div className='form-group mb-4'>
                                                        <label className='small font-weight-bold text-uppercase form-label-uppercase'>{labels?.email?.[languageObj?.id]}</label>
                                                        <div className='position-relative'>
                                                            <i className={`${icons?.fasFa?.envelope} position-absolute input-icon-left`} />
                                                            <input className='form-control border-0 shadow-sm form-input-icon'
                                                                onChange={(e) => setAccount({ ...account, [dbFields?.email]: e.target.value })}
                                                                onFocus={() => setErrors(prev => ({ ...prev, email: '' }))}
                                                                placeholder={labels?.emailPlaceholder?.[languageObj?.id]}
                                                                autoComplete='email'
                                                                title={labels?.email?.[languageObj?.id]}
                                                                value={account?.email || ''} />
                                                        </div>
                                                        <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.email)}</div>
                                                    </div>

                                                    <div className='form-group mb-4'>
                                                        <label className='small font-weight-bold text-uppercase form-label-uppercase'>{labels?.password?.[languageObj?.id]}</label>
                                                        <div className='position-relative'>
                                                            <i className={`${icons?.fasFa?.lock} position-absolute input-icon-left`} />
                                                            <input className='form-control border-0 shadow-sm form-input-icon-both'
                                                                onChange={(e) => setAccount({ ...account, [dbFields?.password]: e.target.value, })}
                                                                onFocus={() => setErrors(prev => ({ ...prev, password: '' }))}
                                                                placeholder='••••••••'
                                                                title={labels?.password?.[languageObj?.id]}
                                                                type={showPassword ? 'text' : 'password'}
                                                                autoComplete='current-password'
                                                                value={account?.password || ''} />
                                                            <span className='position-absolute input-icon-right'
                                                                onClick={() => setShowPassword(!showPassword)}>
                                                                <i className={showPassword ? `${icons?.fasFa?.eye}` : `${icons?.fasFa?.eyeSlash}`}></i>
                                                            </span>
                                                        </div>
                                                        <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.password)}</div>
                                                    </div>

                                                    <div className='form-group mb-4'>
                                                        <label className='small font-weight-bold text-uppercase form-label-uppercase'>{labels?.passwordConfirm?.[languageObj?.id]}</label>
                                                        <div className='position-relative'>
                                                            <i className={`${icons?.fasFa?.lock} position-absolute input-icon-left`} />
                                                            <input className='form-control border-0 shadow-sm form-input-icon-both'
                                                                onChange={(e) => setAccount({ ...account, 'passwordConfirm': e.target.value, })}
                                                                onFocus={() => setErrors(prev => ({ ...prev, passwordConfirm: '' }))}
                                                                value={account?.passwordConfirm || ''}
                                                                type={showPassword ? 'text' : 'password'}
                                                                placeholder='••••••••'
                                                                autoComplete='new-password'
                                                                title={labels?.passwordConfirm?.[languageObj?.id]} />
                                                            <span className='position-absolute input-icon-right'
                                                                onClick={() => setShowPassword(!showPassword)}>
                                                                <i className={showPassword ? `${icons?.fasFa?.eye}` : `${icons?.fasFa?.eyeSlash}`}></i>
                                                            </span>
                                                        </div>
                                                        <div className='form-text mt-1 form-error-text'>{HtmlParser(errors?.passwordConfirm)}</div>
                                                        {/* <div className='mt-4 d-flex flex-column'>
                                                            <div className='mb-2'>
                                                                <i className={`${icons?.fasFa?.lock} mr-2 text-primary`} />
                                                                <a href='/forgotPassword' id='lnkForgotPassword' className='link-primary-bold small'>{labels?.forgotYourPassword?.[languageObj?.id]}</a>
                                                            </div>
                                                            <div>
                                                                <i className={`${icons?.userClock} mr-2 text-primary`} />
                                                                <a href='/registerStatus' id='lnkRegisterStatus' className='link-primary-bold small'>{labels?.checkRegistrationStatus?.[languageObj?.id]}</a>
                                                            </div>
                                                        </div> */}
                                                    </div>
                                                </div>

                                                <div className='card-footer bg-white border-top-0 pb-4 px-4'>
                                                    <div className='form-group mb-3'>
                                                        <button data-title={labels?.create?.[languageObj?.id]}
                                                            data-toggle='tooltip'
                                                            type='submit'
                                                            disabled={process?.showLoading}
                                                            className='btn btn-primary btn-block shadow-sm font-weight-bold btn-primary py-2'>
                                                            {!process?.save ? (
                                                                <><i className={`${icons?.fasFa?.save} mr-2`} /> {labels?.create?.[languageObj?.id]}</>
                                                            ) : (
                                                                <span className={`${settings?.react?.html?.spinner?.borderSm}`} aria-hidden='true'></span>
                                                            )}
                                                        </button>
                                                        <button data-title={labels?.login?.[languageObj?.id]}
                                                            data-toggle='tooltip'
                                                            type='button'
                                                            disabled={process?.showLoading}
                                                            onClick={(e) => goTo({ e: e, subMethod: 'btnLogin' })}
                                                            className='btn btn-light btn-block border shadow-sm font-weight-bold px-4 text-secondary btn-general'>
                                                            {!process?.login ? (
                                                                <><i className={`${icons?.fasFa?.powerOff} mr-2`} /> {labels?.login?.[languageObj?.id]}</>
                                                            ) : (
                                                                <span className={`${settings?.react?.html?.spinner?.borderSm}`} aria-hidden='true'></span>
                                                            )}
                                                        </button>
                                                    </div>
                                                    {/* <div className='row px-2'>
                                                        <div className='col-6 p-1'>
                                                            <a title='Home' href={urls?.home} id='btnHome' className='btn btn-light border btn-block shadow-sm font-weight-bold text-secondary btn-general py-2'>
                                                                <i className={`${icons?.fasFa?.home} mr-2`} /> {labels?.home?.[languageObj?.id]}
                                                            </a>
                                                        </div>
                                                        <div className='col-6 p-1'>
                                                            <a title='LogIn' href='/login' id='btnLogin' className='btn btn-light border btn-block shadow-sm font-weight-bold text-secondary btn-general py-2'>
                                                                <i className={`${icons?.fasFa?.powerOff} mr-2`} /> {labels?.login?.[languageObj?.id]}
                                                            </a>
                                                        </div>
                                                    </div> */}
                                                </div>
                                            </form>

                                            <div className='px-4 pb-4 text-center border-top pt-3 login-register-section'>
                                                <p className='small text-muted mb-2'>{labels?.registerNewMembership?.[languageObj?.id]}</p>
                                                <div className='d-flex justify-content-center gap-2 flex-wrap'>
                                                    {/* <a href='/forgotPassword' className='btn btn-light border shadow-sm  mx-1 font-weight-bold text-secondary'>
                                                        <i className={`${icons?.fasFa?.userCheck} mr-1 text-primary`} /> {labels?.forgotYourPassword?.[languageObj?.id]}
                                                    </a> */}
                                                    <a href='/registerStatus' data-title={labels?.myStatus?.[languageObj?.id]} className='btn btn-light border shadow-sm mx-1 font-weight-bold text-secondary'>
                                                        <i className={`${icons?.userClock} mr-1 text-warning`} /> {labels?.myStatus?.[languageObj?.id]}
                                                    </a>
                                                </div>
                                            </div>

                                            {/* <div> */}
                                            <ReCAPTCHA className='g-recaptcha'
                                                ref={reCaptchaRef}
                                                sitekey={settings?.app?.reCAPTCHA?.reCaptchaSiteKey}
                                                hl={languageObj?.id}
                                                size='invisible' />
                                            {/* </div> */}
                                        </div>

                                        <div className='text-muted text-center text-small'>
                                            <p>
                                                <small>
                                                    {labels?.protectedByReCAPTCHA?.[languageObj?.id]} - <a target='_blank'
                                                        href={settings?.app?.reCAPTCHA?.urlPrivaciConditions}
                                                        rel='noreferrer'>{labels?.privacyAndConditions?.[languageObj?.id]}
                                                    </a>
                                                </small>
                                            </p>
                                        </div>
                                    </div>

                                ) : (
                                    <div className='login-box login-box-container'>
                                        <RegisterNewUserMessage languageObj={languageObj}
                                            account={account} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>{/* /.container-fluid */}
                </section>
                {/* /.content */}
            </div>
        </div>
    )
}